import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getAllProjectsDataParser } from 'parser/pricingSimulator/ProjectsScenariosParser';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
const errorMessage: string = 'Something went wrong!';

// api with parser logic
interface Props {
    url: string;
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onComplete?: Function;
    onError?: Function;
    variables?: any;
    extras?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}
interface outputType {
    data: any;
    loading: boolean;
    error: any;
    count: number;
    setData: Function;
}

function useFetchGetAllProjects({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    extras = {},
}: Props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    const { appConfig } = useContext(UserContext);
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { newlyCreatedProjectId, setNewlyCreatedProjectId, setExpandedKeys, setAllScenariosTableVariables } =
                extras;
            const { projects_list: projectsResult, total_count } = payload;
            let mergedDataRes: any = [];
            if (projectsResult?.length === 0) {
                throw 'No Data';
            }
            const parsedData = getAllProjectsDataParser(projectsResult, appConfig, dimensionConfigData);
            //if  offset is different from default offset value, it means request coming from infinite scroll
            if (variables?.offset !== 0 && Array.isArray(data)) {
                //merge total sales, old data, new data
                mergedDataRes = [...data, ...parsedData]; //remove total
            } else mergedDataRes = parsedData;
            //when new project is created, expand that project by default
            if (newlyCreatedProjectId) {
                setExpandedKeys({ [String(newlyCreatedProjectId)]: true });
                setAllScenariosTableVariables({ projectId: newlyCreatedProjectId });
                setNewlyCreatedProjectId(null);
            } else {
                setExpandedKeys({});
                setAllScenariosTableVariables({});
            }
            setData(mergedDataRes);
            setCount(total_count || 0);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
        setData,
    };
    return outputData;
}

export { useFetchGetAllProjects };
